import {
  GenericTable,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import EmptyState from "components/EmptyState";
import { modalTypes } from "constants/types";
import { useDebouncedScroll } from "hooks";
import { FilePlus, FileX2Icon } from "lucide-react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hideModal } from "slices/app/actions/modals";
import { fetchWorkspaceDocuments } from "slices/workspace/workspaceV2Actions";
import {
  selectWorkspaceDocuments,
  selectWorkspaceDocumentsIsLastPage,
  selectWorkspaceDocumentsIsLoading,
  selectWorkspaceDocumentsIsLoadingMore,
  selectWorkspaceDocumentsPageNumber,
} from "slices/workspace/workspaceV2Selectors";
import { getAddDocumentsRoute } from "utils/entityDocumentRoutes";
import { isDocumentWordless } from "utils/reportSelectability";

import classes from "./ChooseShredDocumentModal.module.scss";
import { buildColumns } from "./columns";
import WorkspaceToolbar from "./WorkspaceToolbar";

const ENTITY_TYPE_LABELS = {
  PROPOSAL: "proposal",
  OPPORTUNITY: "opportunity",
  CONTRACT: "contract",
  MISC_ANALYSIS: "analysis",
};

const getEmptyStateContent = ({
  showWorkspace,
  searchFilter,
  entityType,
  isExcel,
}) => {
  const docLabel = isExcel ? "excels" : "documents";
  const entityLabel = ENTITY_TYPE_LABELS[entityType] || "resource";
  const title = `No ${isExcel ? "Excels" : "Documents"}`;

  if (showWorkspace) {
    if (searchFilter) {
      return {
        title: "No Matches",
        description: `No ${docLabel} match your search.`,
        showAddAction: false,
      };
    }
    return {
      title,
      description: `No ${docLabel} are available in the workspace.`,
      showAddAction: true,
    };
  }

  return {
    title,
    description: `No ${docLabel} have been added to this ${entityLabel} yet.`,
    showAddAction: true,
  };
};

const ChooseShredDocumentModal = ({
  entityDocuments = [],
  selectedDocument: initialSelectedDocument,
  initialShowWorkspace = false,
  entityName,
  entityType = "PROPOSAL",
  entityId,
  onConfirm,
  modalTitle = "Choose A Document To Shred",
  filterFn,
  revisionType = "document",
  canAddDocuments = true,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const workspaceDocs = useSelector(selectWorkspaceDocuments);
  const isLoading = useSelector(selectWorkspaceDocumentsIsLoading);
  const isLoadingMore = useSelector(selectWorkspaceDocumentsIsLoadingMore);
  const isLastPage = useSelector(selectWorkspaceDocumentsIsLastPage);
  const pageNumber = useSelector(selectWorkspaceDocumentsPageNumber);
  const isExcel = revisionType === "excel";

  const addDocumentsRoute = getAddDocumentsRoute(entityType, entityId);

  const [showWorkspace, setShowWorkspace] = useState(
    initialShowWorkspace || false
  );
  const [selectedDocument, setSelectedDocument] = useState(
    initialSelectedDocument || null
  );
  const [searchFilter, setSearchFilter] = useState("");
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);

  const proposalDocIds = useMemo(
    () => new Set(entityDocuments.map((d) => d.id)),
    [entityDocuments]
  );

  const dispatchWorkspaceFetch = useCallback(
    ({
      reset = false,
      search = searchFilter,
      nextSorting = sorting,
      page = 0,
    } = {}) => {
      const primary = nextSorting?.[0];
      dispatch(
        fetchWorkspaceDocuments({
          reset,
          search,
          pageNumber: page,
          sortColumn: primary?.id ?? "name",
          sortOrder: primary?.desc ? "DESC" : "ASC",
          entityType,
          entityId,
        })
      );
    },
    [dispatch, entityId, entityType, searchFilter, sorting]
  );

  useEffect(() => {
    if (showWorkspace) {
      dispatchWorkspaceFetch({ reset: true });
    }
  }, [showWorkspace]);

  const handleToggle = (checked) => {
    if (
      !checked &&
      selectedDocument &&
      !proposalDocIds.has(selectedDocument.id)
    ) {
      setSelectedDocument(null);
    }
    if (checked) {
      setSorting([{ id: "locationType", desc: false }]);
    }
    setShowWorkspace(checked);
    setSearchFilter("");
  };

  const handleSearch = (search) => {
    setSearchFilter(search);
    dispatchWorkspaceFetch({ reset: true, search });
  };

  const handleSortingChange = (next) => {
    setSorting(next);
    dispatchWorkspaceFetch({ reset: true, nextSorting: next });
  };

  const handleScroll = useCallback(() => {
    if (isLastPage || isLoading || isLoadingMore) {
      return;
    }
    dispatchWorkspaceFetch({ page: pageNumber + 1 });
  }, [
    isLastPage,
    isLoading,
    isLoadingMore,
    pageNumber,
    dispatchWorkspaceFetch,
  ]);

  const onDebouncedScroll = useDebouncedScroll({
    callback: handleScroll,
    dependencies: [isLastPage, isLoading, isLoadingMore, pageNumber],
  });

  const handleClose = () => {
    dispatch(hideModal(modalTypes.CHOOSE_SHRED_DOCUMENT));
  };

  const handleConfirm = () => {
    if (selectedDocument) {
      onConfirm(selectedDocument);
    }
    handleClose();
  };

  const handleAddDocuments = () => {
    if (!addDocumentsRoute) {
      return;
    }

    handleClose();
    navigate(addDocumentsRoute);
  };

  const visibleWorkspaceDocs = filterFn
    ? workspaceDocs.filter(filterFn)
    : workspaceDocs;

  const displayDocuments = showWorkspace
    ? visibleWorkspaceDocs
    : entityDocuments;

  const columns = useMemo(
    () =>
      buildColumns({
        proposalDocIds,
        showWorkspace,
        entityType,
      }),
    [selectedDocument, proposalDocIds, showWorkspace, entityType]
  );

  const renderDocumentList = () => {
    const isEmpty = showWorkspace
      ? !isLoading && displayDocuments.length === 0
      : displayDocuments.length === 0;

    if (isEmpty) {
      const { title, description, showAddAction } = getEmptyStateContent({
        showWorkspace,
        searchFilter,
        entityType,
        isExcel,
      });

      const showAddButton =
        showAddAction && canAddDocuments && Boolean(addDocumentsRoute);

      return (
        <div className={classes.choose_shred_doc__empty}>
          <EmptyState
            icon={<FileX2Icon size={24} color="#533ffb" />}
            title={title}
            description={description}
            buttons={
              showAddButton && (
                <NewButton
                  variant={NewButtonVariant.l2}
                  leftIcon={<FilePlus size={16} />}
                  onClick={handleAddDocuments}
                >
                  Go to Documents
                </NewButton>
              )
            }
          />
        </div>
      );
    }

    return (
      <div className={classes["choose_shred_doc__table"]}>
        <GenericTable
          key={showWorkspace ? "workspace" : "proposal"}
          data={displayDocuments}
          columns={columns}
          defaultSortColumnIndex={showWorkspace ? -1 : 0}
          cellClassName={classes["choose_shred_doc__cell"]}
          headerCellClassName={classes["choose_shred_doc__header_cell"]}
          rowClassName={classes["choose_shred_doc__row"]}
          onRowClick={(doc) => {
            if (!isDocumentWordless(doc)) {
              setSelectedDocument(doc);
            }
          }}
          isRowDisabled={isDocumentWordless}
          isVirtualized={showWorkspace}
          loading={isLoading}
          loadingMore={isLoadingMore}
          onScroll={showWorkspace ? onDebouncedScroll : undefined}
          manualSorting={showWorkspace}
          onSortingChange={showWorkspace ? handleSortingChange : undefined}
          enableRowSelection
          rowIdKey="id"
          selectedRowIds={selectedDocument ? [selectedDocument.id] : []}
          selectedRowClassName={
            classes["choose_shred_doc__previously_selected_row"]
          }
        />
      </div>
    );
  };

  return (
    <Modal
      className="choose_shred_doc"
      onDismiss={handleClose}
      restrictWidth={false}
      backdrop
      show
    >
      <Modal.Header>{modalTitle}</Modal.Header>

      <Modal.Body>
        <WorkspaceToolbar
          showWorkspace={showWorkspace}
          onToggle={handleToggle}
          onSearch={handleSearch}
          entityName={entityName}
        />
        {renderDocumentList()}
      </Modal.Body>

      <Modal.Footer>
        <NewButton variant={NewButtonVariant.l2} onClick={handleClose}>
          Cancel
        </NewButton>
        <NewButton
          variant={NewButtonVariant.l1}
          disabled={!selectedDocument}
          onClick={handleConfirm}
        >
          Confirm
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

ChooseShredDocumentModal.propTypes = {
  entityDocuments: arrayOf(
    shape({
      id: number.isRequired,
      name: string.isRequired,
      source: string,
      wordCount: number,
    })
  ),
  selectedDocument: shape({
    id: number.isRequired,
    name: string.isRequired,
  }),
  initialShowWorkspace: bool,
  entityName: string,
  entityType: string,
  entityId: number,
  onConfirm: func.isRequired,
  modalTitle: string,
  filterFn: func,
  revisionType: string,
  canAddDocuments: bool,
};

export default ChooseShredDocumentModal;
